// ---------------------------------------------------------------------------
// Template schema, section factories and the builder reducer.
//
// The reducer state IS the persisted JSON document (minus transient UI state
// like the current selection, which the page holds separately).
// ---------------------------------------------------------------------------

import {
  getDerived,
  domainForSource,
  sourceLabel,
  scalarFields,
  collectionFields,
  RECORD_LIST_PATH,
} from "./fieldCatalog";

export const SCHEMA_VERSION = 1;

// Palette of section blocks the user can add. `icon` is resolved to a MUI icon
// in SectionPalette.
export const SECTION_TYPES = [
  { type: "TITLE", label: "Title", icon: "title", hint: "Banner heading" },
  { type: "DETAIL_FIELDS", label: "Detail fields", icon: "detail", hint: "Label + value" },
  { type: "DATA_TABLE", label: "Data table", icon: "table", hint: "Repeats rows" },
  { type: "FOOTER_TOTALS", label: "Totals", icon: "totals", hint: "Footer summary" },
  { type: "TEXT", label: "Text note", icon: "text", hint: "Free text" },
  { type: "SPACER", label: "Spacer", icon: "spacer", hint: "Blank row" },
];

let counter = 0;
export const newId = (prefix = "sec") =>
  `${prefix}-${Date.now().toString(36)}-${(counter++).toString(36)}`;

const isNumeric = (type) => type === "number" || type === "currency";
// `level` marks the entity a column reads from: "PARENT" (the record itself) or
// a collection path (e.g. "lineItems[]"). It lets a table mix order-level fields
// (repeated per row) with line-item fields.
const colFromField = (field, level = "PARENT") => ({
  header: field.label,
  path: field.path,
  type: field.type,
  align: isNumeric(field.type) ? "right" : "left",
  total: isNumeric(field.type),
  level,
});

export function newSection(type) {
  const base = { id: newId(), type, style: {} };
  switch (type) {
    case "TITLE":
      return {
        ...base,
        text: "REPORT TITLE",
        merge: true,
        style: { bold: true, fontSize: 18, align: "center", fillColor: "FF3B5BD9", fontColor: "FFFFFFFF" },
      };
    case "DETAIL_FIELDS":
      return { ...base, columnsPerRow: 2, fields: [], labelStyle: { bold: true } };
    case "DATA_TABLE":
      return {
        ...base,
        collLabel: "",
        collectionPath: "",
        rowFilter: null,
        columns: [],
        showColumnTotals: false,
        zebra: true,
        headerStyle: { bold: true, fillColor: "FFEEEEEE" },
      };
    case "FOOTER_TOTALS":
      return { ...base, align: "right", rows: [] };
    case "TEXT":
      return { ...base, text: "Free text note" };
    case "SPACER":
      return { ...base, rows: 1 };
    default:
      return base;
  }
}

/** Minimal document with just a title — used for a blank "New template". */
export function newTemplate(source) {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: null,
    name: `${sourceLabel(source)} Report`,
    domain: domainForSource(source),
    source,
    visibility: "PRIVATE",
    sheet: {
      sheetName: "Report",
      columnCount: 4,
      columnWidths: [],
      defaultFont: { name: "Calibri", size: 11 },
      currencyFormat: "#,##0.00",
      dateFormat: "yyyy-mm-dd",
    },
    sections: [newSection("TITLE")],
  };
}

/** A ready-to-use starter (title + details + first collection table + totals). */
export function starterTemplate(source) {
  const tpl = newTemplate(source);
  tpl.sections = [];

  const title = newSection("TITLE");
  title.text = `${sourceLabel(source).toUpperCase()} REPORT`;
  tpl.sections.push(title);

  const scalars = scalarFields(source);
  if (scalars.length) {
    const detail = newSection("DETAIL_FIELDS");
    detail.fields = scalars.slice(0, 4).map((fl) => ({ label: fl.label, path: fl.path, type: fl.type }));
    tpl.sections.push(detail);
  }

  const colls = collectionFields(source);
  if (colls.length) {
    const first = colls[0];
    const table = newSection("DATA_TABLE");
    table.collLabel = first.label;
    table.collectionPath = first.path;
    table.columns = (first.children || []).slice(0, 4).map((ch) => colFromField(ch, first.path));
    table.showColumnTotals = table.columns.some((c) => c.total);
    tpl.sections.push(table);
  }

  const derived = getDerived(source);
  const derivedKeys = Object.keys(derived);
  if (derivedKeys.length) {
    const footer = newSection("FOOTER_TOTALS");
    footer.rows = derivedKeys.map((k) => ({
      label: derived[k].label,
      derived: k,
      type: "currency",
      style: { bold: true },
    }));
    tpl.sections.push(footer);
  }

  return tpl;
}

// --------------------------- drop rules ---------------------------

/** Returns a message if a field can't be added to a table, else null. */
export function tableFieldConflict(section, field) {
  if (!section || section.type !== "DATA_TABLE" || !field || field.isCollection) return null;
  const level = field._collectionPath || "PARENT";
  if (level === "PARENT") return null; // order-level fields always allowed
  const grain = section.collectionPath;
  if (grain === RECORD_LIST_PATH) return "This table lists one row per record — add record-level fields only.";
  if (grain && grain !== level)
    return "A table can use one collection (e.g. Line Items OR Payments) plus order-level fields. Remove the other collection's columns first.";
  return null;
}

/** Can a dragged field be dropped on this section type? */
export function acceptsField(sectionType, field) {
  if (!field) return false;
  if (sectionType === "DATA_TABLE") return true; // collection binds; scalar adds a column
  if (sectionType === "DETAIL_FIELDS" || sectionType === "FOOTER_TOTALS")
    return !field.isCollection;
  if (sectionType === "TITLE" || sectionType === "TEXT") return !field.isCollection;
  return false;
}

// --------------------------- reducer ---------------------------

function mapSection(template, id, fn) {
  return { ...template, sections: template.sections.map((s) => (s.id === id ? fn(s) : s)) };
}

function applyFieldToSection(template, id, field) {
  return mapSection(template, id, (s) => {
    if (s.type === "DETAIL_FIELDS") {
      if (field.isCollection) return s;
      return { ...s, fields: [...(s.fields || []), { label: field.label, path: field.path, type: field.type }] };
    }
    if (s.type === "FOOTER_TOTALS") {
      if (field.isCollection) return s;
      return { ...s, rows: [...(s.rows || []), { label: field.label, path: field.path, type: field.type }] };
    }
    if (s.type === "DATA_TABLE") {
      // Dropping a whole collection (re)binds the table's grain to it, keeping
      // any order-level (PARENT) columns already added.
      if (field.isCollection) {
        const parentCols = (s.columns || []).filter((c) => c.level === "PARENT");
        const childCols = (field.children || []).slice(0, 5).map((ch) => colFromField(ch, field.path));
        return { ...s, collLabel: field.label, collectionPath: field.path, rowFilter: null, columns: [...parentCols, ...childCols] };
      }
      const level = field._collectionPath || "PARENT";
      const grain = s.collectionPath;
      // A within-record collection can't go on a per-record list table, and two
      // sibling collections can't share one table (see tableFieldConflict).
      if (level !== "PARENT" && grain === RECORD_LIST_PATH) return s;
      if (level !== "PARENT" && grain && grain !== RECORD_LIST_PATH && grain !== level) return s;
      return {
        ...s,
        collectionPath: level !== "PARENT" ? level : grain || "",
        collLabel: level !== "PARENT" ? field._collLabel || s.collLabel : s.collLabel,
        columns: [...(s.columns || []), colFromField(field, level)],
      };
    }
    if (s.type === "TITLE" || s.type === "TEXT") {
      const token = `{{${field.path}}}`;
      return { ...s, text: s.text ? `${s.text} ${token}` : token };
    }
    return s;
  });
}

export function templateReducer(template, action) {
  switch (action.type) {
    case "LOAD_TEMPLATE":
      return action.template;
    case "SET_META":
      return { ...template, ...action.patch };
    case "SET_SHEET":
      return { ...template, sheet: { ...template.sheet, ...action.patch } };
    case "ADD_SECTION": {
      const section = newSection(action.sectionType);
      const sections = [...template.sections];
      const idx = action.index == null || action.index < 0 ? sections.length : action.index;
      sections.splice(idx, 0, section);
      return { ...template, sections, _lastAdded: section.id };
    }
    case "REMOVE_SECTION":
      return { ...template, sections: template.sections.filter((s) => s.id !== action.id) };
    case "MOVE_SECTION": {
      const ids = template.sections.map((s) => s.id);
      const from = ids.indexOf(action.fromId);
      const to = ids.indexOf(action.toId);
      if (from < 0 || to < 0 || from === to) return template;
      const sections = [...template.sections];
      const [moved] = sections.splice(from, 1);
      sections.splice(to, 0, moved);
      return { ...template, sections };
    }
    case "UPDATE_SECTION":
      return mapSection(template, action.id, (s) => ({ ...s, ...action.patch }));
    case "ADD_FIELD_TO_SECTION":
      return applyFieldToSection(template, action.id, action.field);
    case "REMOVE_FIELD":
      return mapSection(template, action.id, (s) => ({
        ...s,
        fields: (s.fields || []).filter((_, i) => i !== action.index),
      }));
    case "ADD_COLUMN":
      return mapSection(template, action.id, (s) => ({ ...s, columns: [...(s.columns || []), action.column] }));
    case "REMOVE_COLUMN":
      return mapSection(template, action.id, (s) => ({
        ...s,
        columns: (s.columns || []).filter((_, i) => i !== action.index),
      }));
    case "MOVE_COLUMN":
      return mapSection(template, action.id, (s) => {
        const cols = [...(s.columns || [])];
        const from = action.index;
        const to = from + action.dir;
        if (from < 0 || to < 0 || from >= cols.length || to >= cols.length) return s;
        const [moved] = cols.splice(from, 1);
        cols.splice(to, 0, moved);
        return { ...s, columns: cols };
      });
    case "UPDATE_COLUMN":
      return mapSection(template, action.id, (s) => ({
        ...s,
        columns: (s.columns || []).map((c, i) => (i === action.index ? { ...c, ...action.patch } : c)),
      }));
    case "ADD_FOOTER_ROW":
      return mapSection(template, action.id, (s) => ({ ...s, rows: [...(s.rows || []), action.row] }));
    case "REMOVE_FOOTER_ROW":
      return mapSection(template, action.id, (s) => ({
        ...s,
        rows: (s.rows || []).filter((_, i) => i !== action.index),
      }));
    default:
      return template;
  }
}
